/**
 * 不可变 DXF 导出配置。模块初始化时校验注册表。
 */
import { ALL_LAYER_ROLES, DxfLayerRole, DxfProfileContract } from './types'
import type { DxfProfile, DxfProfileId, LayerMap } from './types'

const ACI_WHITE = 7
const ACI_RED = 1
const ACI_GREEN = 3
const ACI_CYAN = 4
const ACI_MAGENTA = 6
const CONTINUOUS = 'Continuous'

function deepFreeze<T>(obj: T): T {
  if (obj == null || typeof obj !== 'object') return obj
  Object.freeze(obj)
  for (const v of Object.values(obj as unknown as object)) {
    deepFreeze(v)
  }
  return obj
}

/* ------------------------------------------------------------------ */
/*  图层映射                                                            */
/* ------------------------------------------------------------------ */

const LEGACY_LAYERS: LayerMap = deepFreeze({
  [DxfLayerRole.BigPlank]: {
    name: 'BigPlank',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.Parts]: {
    name: 'Parts',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.HolesFront]: {
    name: 'HolesFront',
    color: ACI_GREEN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.HolesBack]: {
    name: 'HolesBack',
    color: ACI_RED,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.SlotsFront]: {
    name: 'SlotsFront',
    color: ACI_GREEN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.SlotsBack]: {
    name: 'SlotsBack',
    color: ACI_RED,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.CurveHolesFront]: {
    name: 'CurveHolesFront',
    color: ACI_CYAN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.CurveHolesBack]: {
    name: 'CurveHolesBack',
    color: ACI_MAGENTA,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.MillInfoFront]: {
    name: 'MillInfoFront',
    color: ACI_CYAN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.MillInfoBack]: {
    name: 'MillInfoBack',
    color: ACI_MAGENTA,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.PartText]: {
    name: 'PartText',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.BigPlankText]: {
    name: 'BigPlankText',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.OverflowText]: {
    name: 'OverflowText',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.OverflowLine]: {
    name: 'OverflowLine',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
})

const CUSTOMER_LAYERS: LayerMap = deepFreeze({
  [DxfLayerRole.BigPlank]: {
    name: 'BigPlank',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.Parts]: {
    name: 'PanelInfo',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.HolesFront]: {
    name: 'Drill_XY1',
    color: ACI_GREEN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.HolesBack]: {
    name: 'Drill_XY0',
    color: ACI_RED,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.SlotsFront]: {
    name: 'Groove_XY1',
    color: ACI_GREEN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.SlotsBack]: {
    name: 'Groove_XY0',
    color: ACI_RED,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.CurveHolesFront]: {
    name: 'Mill_XY1',
    color: ACI_CYAN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.CurveHolesBack]: {
    name: 'Mill_XY0',
    color: ACI_MAGENTA,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.MillInfoFront]: {
    name: 'MillInfoFront',
    color: ACI_CYAN,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.MillInfoBack]: {
    name: 'MillInfoBack',
    color: ACI_MAGENTA,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.PartText]: {
    name: 'PartText',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.BigPlankText]: {
    name: 'BigPlankText',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.OverflowText]: {
    name: 'OverflowText',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
  [DxfLayerRole.OverflowLine]: {
    name: 'OverflowLine',
    color: ACI_WHITE,
    lineType: CONTINUOUS,
  },
})

/* ------------------------------------------------------------------ */
/*  配置定义                                                            */
/* ------------------------------------------------------------------ */

const _general2dV1: DxfProfile = {
  id: 'general-2d-v1',
  label: 'Default (2D legacy layers)',
  layerMap: LEGACY_LAYERS,
  contract: DxfProfileContract.Planar2D,
  sideDrill: false,
  sideDrillView: false,
}

const _specialized3dV1: DxfProfile = {
  id: 'specialized-3d-v1',
  label: 'Machining thickness (3D OCS, DXF 2.8.9 fix)',
  layerMap: CUSTOMER_LAYERS,
  contract: DxfProfileContract.ThicknessOCS,
  // 侧孔/侧槽实体暂时隐藏
  sideDrill: false,
  sideDrillView: false,
}

const SUPPORTED_CONTRACTS: ReadonlySet<string> = new Set(
  Object.values(DxfProfileContract)
)

const _rawProfiles: readonly DxfProfile[] = [_general2dV1, _specialized3dV1]

export const DEFAULT_PROFILE_ID: DxfProfileId = 'general-2d-v1'

/* ------------------------------------------------------------------ */
/*  注册表校验（模块初始化时执行）                                        */
/* ------------------------------------------------------------------ */

function validateRegistry(profiles: readonly DxfProfile[]): void {
  const ids = new Set<string>()

  for (const p of profiles) {
    if (ids.has(p.id)) {
      throw new Error(`[dxf-profiles] Duplicate profile id: "${p.id}"`)
    }
    ids.add(p.id)

    if (!SUPPORTED_CONTRACTS.has(p.contract)) {
      throw new Error(
        `[dxf-profiles] Profile "${p.id}" has unknown contract "${p.contract}"`
      )
    }

    if (typeof p.sideDrill !== 'boolean') {
      throw new Error(
        `[dxf-profiles] Profile "${p.id}" sideDrill must be boolean`
      )
    }
    if (typeof p.sideDrillView !== 'boolean') {
      throw new Error(
        `[dxf-profiles] Profile "${p.id}" sideDrillView must be boolean`
      )
    }
    if (p.sideDrillView && !p.sideDrill) {
      throw new Error(
        `[dxf-profiles] Profile "${p.id}" sideDrillView requires sideDrill`
      )
    }
    if (p.sideDrillView && p.contract !== DxfProfileContract.ThicknessOCS) {
      throw new Error(
        `[dxf-profiles] Profile "${p.id}" sideDrillView requires ThicknessOCS contract`
      )
    }

    for (const role of ALL_LAYER_ROLES) {
      const def = p.layerMap[role]
      if (!def) {
        throw new Error(
          `[dxf-profiles] Profile "${p.id}" missing layer role "${role}"`
        )
      }
      if (typeof def.name !== 'string' || def.name.trim() === '') {
        throw new Error(
          `[dxf-profiles] Profile "${p.id}" role "${role}" has empty layer name`
        )
      }
    }
  }

  const defaults = profiles.filter((p) => p.id === DEFAULT_PROFILE_ID)
  if (defaults.length !== 1) {
    throw new Error(
      `[dxf-profiles] Expected exactly 1 profile with id "${DEFAULT_PROFILE_ID}", found ${defaults.length}`
    )
  }
}

validateRegistry(_rawProfiles)

export const PROFILES: readonly DxfProfile[] = Object.freeze(
  _rawProfiles.map((p) => deepFreeze(p))
)
